import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, Target, Heart, Lightbulb, Users, Shield, Zap, CheckCircle } from 'lucide-react';

const Sobre = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* ===== NAVBAR ===== */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/">
            <img src="/lembrou-logo-png.png" alt="Lembrou?" className="h-9" />
          </Link>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/auth?mode=business')}
              variant="ghost"
              className="rounded-full text-gray-600 hover:text-primary px-4 text-sm font-medium hidden sm:flex"
            >
              Entrar
            </Button>
            <Button
              onClick={() => navigate('/auth?mode=business')}
              className="rounded-full bg-primary hover:bg-primary-hover text-white px-6 text-sm font-semibold"
            >
              Começar Grátis
            </Button>
          </div>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-red-50/30 to-white py-16 lg:py-24">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-8 rounded-full text-gray-500 hover:text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>

          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Sobre Nós
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
              Nascemos para resolver um problema <span className="text-primary">real</span>
            </h1>
            <p className="text-lg text-gray-500 leading-relaxed">
              O Lembrou? surgiu da necessidade de profissionais que perdiam clientes e receita 
              simplesmente por esquecerem datas importantes de renovação.
            </p>
          </div>
        </div>
      </section>

      {/* ===== NOSSA HISTÓRIA ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
                Nossa História
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                De uma dor real para uma solução prática
              </h2>
              <p className="text-gray-500 mb-6 leading-relaxed">
                Tudo começou quando percebemos que corretores de seguros, advogados, contadores e 
                diversos profissionais perdiam clientes simplesmente por não lembrarem das datas 
                de vencimento de contratos e serviços.
              </p>
              <p className="text-gray-500 mb-6 leading-relaxed">
                A solução era simples: criar um sistema que enviasse lembretes automáticos para 
                os clientes antes do vencimento. Mas queríamos ir além — queríamos que o próprio 
                cliente pudesse se cadastrar facilmente através de um QR Code.
              </p>
              <p className="text-gray-500 leading-relaxed">
                Assim nasceu o <strong className="text-gray-700">Lembrou?</strong> — uma plataforma 
                que une tecnologia e praticidade para que você nunca mais perca um cliente por esquecimento.
              </p>
            </div>
            <div className="relative">
              <img src="/In_sync-lembrou.svg" alt="Nossa História" className="w-full h-auto max-w-lg mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== MISSÃO, VISÃO, VALORES ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              Nossos Pilares
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              O que nos move
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Missão</h3>
              <p className="text-gray-500 leading-relaxed">
                Ajudar profissionais a nunca mais perderem clientes por esquecimento, 
                automatizando lembretes de forma simples e eficiente.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Lightbulb className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Visão</h3>
              <p className="text-gray-500 leading-relaxed">
                Ser a plataforma de lembretes mais utilizada por profissionais no Brasil, 
                revolucionando a forma como gerenciam seus clientes.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Valores</h3>
              <p className="text-gray-500 leading-relaxed">
                Simplicidade, transparência, inovação e compromisso com o sucesso 
                de cada profissional que confia em nós.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== POR QUE ESCOLHER ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              Diferenciais
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Por que escolher o Lembrou?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: 'Rápido', desc: 'Configure em menos de 5 minutos' },
              { icon: Shield, title: 'Seguro', desc: 'Seus dados protegidos com criptografia' },
              { icon: Users, title: 'Simples', desc: 'Interface intuitiva e fácil de usar' },
              { icon: CheckCircle, title: 'Confiável', desc: '99.9% de uptime garantido' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Faça parte dessa transformação
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Junte-se a centenas de profissionais que já confiam no Lembrou?
          </p>
          <Button
            onClick={() => navigate('/auth?mode=business')}
            className="rounded-full bg-white text-primary hover:bg-gray-100 px-8 py-6 text-base font-semibold shadow-lg"
          >
            Criar Conta Grátis
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10">
            <div>
              <img src="/lembrou-logo-png.png" alt="Lembrou?" className="h-8 mb-4 brightness-0 invert" />
              <p className="text-sm text-gray-400">
                2026 © Lembrou.com.br
                <br />Todos os direitos reservados.
              </p>
            </div>
            <div>
              <p className="font-semibold mb-4 text-sm">Central do Corretor</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => navigate('/auth?mode=business')} className="hover:text-white transition-colors">Cadastre-se</button></li>
                <li><button onClick={() => navigate('/auth?mode=business')} className="hover:text-white transition-colors">Login</button></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-4 text-sm">Institucional</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/sobre" className="hover:text-white transition-colors">Sobre o Lembrou?</Link></li>
                <li><Link to="/planos" className="hover:text-white transition-colors">Planos e Preços</Link></li>
                <li><Link to="/como-funciona" className="hover:text-white transition-colors">Como Funciona</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-4 text-sm">Usuário</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => navigate('/timeline')} className="hover:text-white transition-colors">Minha Timeline</button></li>
                <li><button onClick={() => navigate('/auth?mode=user')} className="hover:text-white transition-colors">Criar Conta Gratuita</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <span className="text-xs text-gray-600">© 2026 Lembrou — Todos os direitos reservados</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Sobre;
